import { FC, useEffect, useState } from "react";
import { Segmented, SimpleCard, Spin } from "../../../design-system";

import {
    Project,
    ProjectAnalysis,
    RfxAnalysisOutput,
} from "../../../types/ProjectTypes";
import { Tactic } from "../../../types/TacticTypes";
import { splitContentByH2 } from "../../../common/api/TacticClient";
import { TacticContent } from "../../tactic/detail-v2/TacticContent";
import { getAnalysisOutput } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { AttachmentsList } from "./AttachmentsList";
import styles from "../ProjectView.module.less";

interface RfxOutputProps {
    project: Project;
    analysis: ProjectAnalysis;
}

enum ViewTab {
    Analysis = "Analysis",
    Attachments = "Attachments",
}

export const RfxOutput: FC<RfxOutputProps> = ({ project, analysis }) => {
    const [output, setOutput] = useState<RfxAnalysisOutput>();

    useEffect(() => {
        let cancelled = false;
        getAnalysisOutput(project.id, analysis.version).then(
            (fetched) => {
                if (cancelled) return;
                setOutput(fetched as RfxAnalysisOutput);
            },
            (error) => {
                if (cancelled) return;
                handleError(error);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [project.id, analysis.version, analysis.recordUpdateDate]);

    if (!output) {
        return (
            <div className={styles.tabContent}>
                <Spin />
            </div>
        );
    }

    return <RfxOutputBody project={project} analysis={analysis} output={output} />;
};

interface RfxOutputBodyProps {
    project: Project;
    analysis: ProjectAnalysis;
    output: RfxAnalysisOutput;
}

const RfxOutputBody: FC<RfxOutputBodyProps> = ({ project, analysis, output }) => {
    const showAttachmentsTab = (analysis.attachments?.length ?? 0) > 0;
    const [activeTab, setActiveTab] = useState<ViewTab>(ViewTab.Analysis);

    const rfxTactic = splitContentByH2({
        ...project.tactic,
        content: output.report?.content,
    } as Tactic);

    return (
        <>
            {showAttachmentsTab && (
                <div className={styles.tabRow}>
                    <Segmented
                        options={[
                            { label: ViewTab.Analysis, value: ViewTab.Analysis },
                            { label: ViewTab.Attachments, value: ViewTab.Attachments },
                        ]}
                        value={activeTab}
                        onChange={(value) => setActiveTab(value as ViewTab)}
                    />
                </div>
            )}

            {activeTab === ViewTab.Analysis && (
                <div className={styles.tabContent}>
                    <SimpleCard header="RFx analysis" headerType="primary">
                        <SimpleCard type="white">
                            <TacticContent tactic={rfxTactic} />
                        </SimpleCard>
                    </SimpleCard>
                </div>
            )}

            {activeTab === ViewTab.Attachments && (
                <div className={styles.tabContent}>
                    <AttachmentsList project={project} analysis={analysis} />
                </div>
            )}
        </>
    );
};
